import { postContactDetails, postStepAPI } from '@_api/quiz';
import { getCurrentRegion } from '@helpers/functions';

interface StepResponse {
  submitted: boolean;
  nextIndex: number;
  progressPercent: number;
  html: string;
  summary?: SummaryItem[];
  summaryConfig?: SummaryConfig;
  showContact: boolean;
}

interface SummaryItem {
  questionId: string;
  questionText: string;
  selectedLabels: string[];
}

interface SummaryConfig {
  heading: string;
  successMessage: string;
  showNumbers: boolean;
  showUnanswered: boolean;
  unansweredLabel: string;
  pillColour: string;
  showRestart: boolean;
  restartLabel: string;
}

interface BlockState {
  current: number;
  total: number;
  multiAnswers: Record<string, string[]>;
}

// ── State store (one entry per block instance on the page) ────────────────────
const blockStates = new Map<string, BlockState>();
let postStepAPIURL = "";
let postContactAPIURL = "";



// ── Entry point ───────────────────────────────────────────────────────────────
function initQuiz(blockId: string, total: number): void {
  blockStates.set(blockId, {
    current: 0,
    total,
    multiAnswers: {},
  });

  bindPanelEvents(blockId);
}

// ── Bind click events to the current panel ────────────────────────────────────
function bindPanelEvents(blockId: string): void {
  const panel = getPanelEl(blockId);
  if (!panel) return;

  bindOptionCards(panel, blockId);
  bindNavButtons(blockId, panel);
  syncNextButton(blockId);   // set initial state on render
}

function bindOptionCards(panel: HTMLElement, blockId: string): void {

  panel.querySelectorAll<HTMLInputElement>(".saig-o-quiz__question-option input[type='checkbox']").forEach((card) => {
    card.addEventListener("click", (event: MouseEvent) => {
      event.stopPropagation();
      const state = blockStates.get(blockId);
      if (state && state.current == 0 && isRecordEmpty(state.multiAnswers)) {
        const chatbotObj = {
          event: 'quiz_start',
          region: getCurrentRegion(),
          quiz_name: "product_finder_quiz"
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(chatbotObj);
      }
      onOptionCheckboxCardClick.call(card);
      syncNextButton(blockId);
    });
  });
}

function isRecordEmpty(obj: unknown): obj is Record<PropertyKey, never> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    Object.keys(obj).length === 0
  );
}
function isCurrentQuestionAnswered(blockId: string): boolean {
  const panel = getPanelEl(blockId);
  if (!panel) return true;
  return !!panel.querySelector<HTMLInputElement>("input[type='checkbox']:checked");

}

function syncNextButton(blockId: string): void {
  const panel = getPanelEl(blockId);
  const btn = panel?.querySelector<HTMLButtonElement>(".saig-o-quiz__nav-btn-next");
  if (!btn) return;
  const answered = isCurrentQuestionAnswered(blockId);
  btn.disabled = !answered;
}

function bindNavButtons(blockId: string, panel: HTMLElement): void {
  const btnBack = panel.querySelector<HTMLButtonElement>(".saig-o-quiz__nav-btn-back");
  const btnNext = panel.querySelector<HTMLButtonElement>(".saig-o-quiz__nav-btn-next");

  btnBack?.addEventListener("click", () => onNavigate(blockId, "back"));
  btnNext?.addEventListener("click", () => onNavigate(blockId, "next"));
}

// ── Option card click ─────────────────────────────────────────────────────────
function onOptionCardClick(this: HTMLElement): void {
  const input = this.querySelector<HTMLInputElement>("input");
  if (!input) return;

  input.checked = !input.checked;
  this.classList.toggle("selected", input.checked);

}

// ── Option card click ─────────────────────────────────────────────────────────
function onOptionCheckboxCardClick(this: HTMLInputElement): void {

  const parentDiv = this.parentElement as HTMLElement | null;
  if (!parentDiv) return;

  parentDiv.classList.toggle("selected", this.checked);

}

// ── Navigation (Back / Next / Submit) ─────────────────────────────────────────
async function onNavigate(blockId: string, direction: "next" | "back"): Promise<void> {
  const state = blockStates.get(blockId);
  if (!state) return;

  // If we're on the contact panel, hand off to contact submit handler
  const panel = getPanelEl(blockId);

  var questionPanel = panel?.querySelector<HTMLDivElement>(".saig-o-contact-form");
  if (questionPanel?.dataset.panelType === "contact") {
    if (direction === "back") {
      await goBackFromContact(blockId);
    } else {
      await submitContact(blockId);
    }
    return;
  }

  collectCurrentAnswers(blockId, state);
  setNextButtonState(blockId, true);

  const payload = {
    BlockContentLink: blockId,
    CurrentIndex: state.current,
    Direction: direction,
    IsContactForm: false,
    MultiAnswers: state.multiAnswers,
  };

  try {
    const data = await postStep(payload);

    if (data.submitted) {
      await swapPanel(blockId, data.html);
      state.current = data.nextIndex;
      renderSummary(blockId);

      return;
    }

    if (data.showContact) {
      const block = getBlockEl(blockId);
      if (!block) return;
      const preogressdiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__progress");
      preogressdiv && (preogressdiv.style.display = "none");

      const titleDiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__titlecontent");
      titleDiv && (titleDiv.style.display = "none");


      // updateProgressBar(blockId, state.total, data.nextIndex, data.progressPercent);
      await swapPanel(blockId, data.html);
      state.current = data.nextIndex;
      bindContactPanel(blockId);
      return;
    }

    if (direction == "next") {
      TriggerQuestionAnsweredEvent(blockId);
    }
    updateProgressBar(blockId, state.total, data.nextIndex, data.progressPercent);
    await swapPanel(blockId, data.html);

    state.current = data.nextIndex;
    bindPanelEvents(blockId);
  } catch (err) {
    console.error("Quiz Step error:", err);
    setNextButtonState(blockId, false);
  }
}

function TriggerQuestionAnsweredEvent(blockId: string): void {
  const panel = getPanelEl(blockId);
  if (!panel) return;

  var questionPanel = panel.querySelector<HTMLDivElement>(".saig-o-quiz__question");
  const quText = questionPanel?.dataset.questionText ?? "";

  const checked = [...panel.querySelectorAll<HTMLInputElement>("input[type='checkbox']:checked")];
  var selected = checked.map((cb) => cb.dataset.name).join(',');

  const quizObject = {
    'event': 'quiz_answer',
    'quiz_name': 'product_finder_quiz',
    region: getCurrentRegion(),
    'question_id': questionPanel?.dataset.questionId ?? "",
    'question_text': quText,
    'answer_value': selected
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(quizObject);

}

function TriggerQuizSubmittedEvent(blockId: string, questionData: string, ansData: string): void {

  const quizObject = {
    'event': 'quiz_complete',
    region: getCurrentRegion(),
    'quiz_name': 'product_finder_quiz',
    'quiz_result_json': ansData,
    // Big Query or Looker Studio can parse this JSON string
    'quiz_ques_answers_json': questionData
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(quizObject);

}

// ── Collect answers from DOM into state ───────────────────────────────────────
function collectCurrentAnswers(blockId: string, state: BlockState): void {
  const panel = getPanelEl(blockId);
  if (!panel) return;

  var questionPanel = panel.querySelector<HTMLDivElement>(".saig-o-quiz__question");


  const qId = questionPanel?.dataset.questionId ?? "";

  const checked = [...panel.querySelectorAll<HTMLInputElement>("input[type='checkbox']:checked")];
  state.multiAnswers[qId] = checked.map((cb) => cb.value);
}
function bindContactPanel(blockId: string): void {
  const panel = getPanelEl(blockId);
  if (!panel) return;
  panel.querySelector<HTMLButtonElement>(".saig-o-contact-form__btn-back")
    ?.addEventListener("click", () => goBackFromContact(blockId));
  panel.querySelector<HTMLButtonElement>(".saig-o-contact-form__btn-next")
    ?.addEventListener("click", () => submitContact(blockId));

  const labelElements = panel?.querySelectorAll<HTMLLabelElement>('.saig-o-contact-form__form-items');
  [].forEach.call(labelElements, (labelElement: HTMLElement) => {

    const elements = labelElement.querySelectorAll('input');

    [].forEach.call(elements, (element: HTMLElement) => {
      element.addEventListener("blur", (e) => validateInput(element, panel, e));
    });

  });
}

// ── Contact form: go back to last question ────────────────────────────────────
async function goBackFromContact(blockId: string): Promise<void> {
  const state = blockStates.get(blockId);
  if (!state) return;

  const payload = {
    BlockContentLink: blockId,
    CurrentIndex: state.current,
    Direction: "back",
    IsContactForm: true,
    MultiAnswers: state.multiAnswers,
  };
  const block = getBlockEl(blockId);
  if (!block) return;
  const preogressdiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__progress");
  preogressdiv && (preogressdiv.style.display = "block");

  const titleDiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__titlecontent");
  titleDiv && (titleDiv.style.display = "flex");


  const data = await postStep(payload);
  updateProgressBar(blockId, state.total, data.nextIndex, data.progressPercent);
  await swapPanel(blockId, data.html);
  state.current = data.nextIndex;
  bindPanelEvents(blockId);
}

function validateInput(element: any, panel: HTMLElement, event: any): void {
  let input = event.target as HTMLInputElement
  var value = input.value;
  var id = input.getAttribute('aria-labelledby') ?? "";

  if (id == "adminEmailAddress") {
    if (!validateEmail(value?.trim())) {
      showError(id, id + '-required-validation', panel);
    } else {
      hideError(id, id + '-required-validation', panel);
    }

  } else if (id == "phoneNumber") {
    if (!validatePhoneNumber(value?.trim())) {
      showError(id, id + '-required-validation', panel);
    } else {
      hideError(id, id + '-required-validation', panel);
    }
  }
  else {
    if (!value?.trim()) {
      showError(id, id + '-required-validation', panel);
    } else {
      hideError(id, id + '-required-validation', panel);
    }
  }

}

function validateEmail(email?: string): any {
  return email
    ? email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    )
    : false;
}
function validatePhoneNumber(phoneNumber?: string): any {
  return phoneNumber
    ? phoneNumber.match(
      /^\(?\+?[0-9]{0,3}\)?[-\s/0-9]+$/
    )
    : false;
}

function showError(inputElement?: string, className?: string, panel?: HTMLElement): void {

  if (inputElement) {
    panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__' + inputElement)?.classList?.add('error-border');
  }
  if (className) {
    panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__form-error' + '.' + className)?.classList?.add('show');
  }

}
function hideError(inputElement?: string, className?: string, panel?: HTMLElement): void {

  if (inputElement) {
    panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__' + inputElement)?.classList?.remove('error-border');
  }
  if (className) {
    panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__form-error' + '.' + className)?.classList?.remove('show');
  }
}

function hideAllError(panel: HTMLElement): void {

  const errorElements = panel?.querySelectorAll<HTMLDivElement>('.saig-o-contact-form__form-error');
  [].forEach.call(errorElements, (errorDiv: HTMLElement) => {
    errorDiv.classList.remove('show');
  });

  const labelElements = panel?.querySelectorAll<HTMLLabelElement>('.saig-o-contact-form__form-items');
  [].forEach.call(labelElements, (labelElement: HTMLElement) => {

    const elements = labelElement.querySelectorAll('input, select');

    [].forEach.call(elements, (element: HTMLElement) => {
      element?.classList.remove('error-border');
    });

  });
}

function validateInputs(panel: HTMLElement): any {
  let valid = true;

  const labelElements = panel?.querySelectorAll<HTMLLabelElement>('.saig-o-contact-form__form-items');
  [].forEach.call(labelElements, (labelElement: HTMLElement) => {

    const elements = labelElement.querySelectorAll('input');

    [].forEach.call(elements, (element: HTMLInputElement) => {
      if (!element.value?.trim()) {
        var id = element.getAttribute('aria-labelledby') ?? "";
        showError(id, id + '-required-validation', panel);
        valid = false;
      }
    });

  });
  return valid;
}

// ── Contact form: collect fields and POST to /quiz / contact ───────────
async function submitContact(blockId: string): Promise<void> {
  const panel = getPanelEl(blockId);
  if (!panel) return;

  const state = blockStates.get(blockId);
  if (!state) return;

  hideAllError(panel);
  if (validateInputs(panel)) {

    const formdata: Record<string, any> = {};

    const labelElements = panel?.querySelectorAll<HTMLLabelElement>('.saig-o-contact-form__form-items');
    [].forEach.call(labelElements, (labelElement: HTMLElement) => {

      const elements = labelElement.querySelectorAll('input');

      [].forEach.call(elements, (element: HTMLInputElement) => {
        var id = element.getAttribute('aria-labelledby') ?? "";
        formdata[id] = element.value?.trim();
      });
    });

    const payload = { BlockContentLink: blockId, ...formdata };
    var api = postContactAPIURL;
    try {
      const res = await postContactDetails(api, JSON.stringify(payload)).then(async (response) => {
        if (typeof response === "string" && response.indexOf('Error') !== -1) {
          showError(undefined, 'server-side-error', panel);
          const genericErrorDiv = panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__form-error' + '.server-side-error' + ' .saig-o-contact-form__form-error-message'
          );
          genericErrorDiv && (genericErrorDiv.innerHTML = response);
        } else {
          const data = response;
          await swapPanel(blockId, data.html);
          state.current = data.nextIndex;
          renderSummary(blockId);
          TriggerQuizSubmittedEvent(blockId, data.questionData, data.ansData);
          return;
        }
      })
        .catch((error) => {
          showError(undefined, 'server-side-error', panel);
          const genericErrorDiv = panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__form-error' + '.server-side-error' + ' .saig-o-contact-form__form-error-message'
          );
          genericErrorDiv && (genericErrorDiv.innerHTML = "Contact submit error:" + error);

        });
    } catch (err) {
      showError(undefined, 'server-side-error', panel);
      const genericErrorDiv = panel?.querySelector<HTMLDivElement>('.saig-o-contact-form__form-error' + '.server-side-error' + ' .saig-o-contact-form__form-error-message'
      );
      genericErrorDiv && (genericErrorDiv.innerHTML = "Contact submit error:" + err);
    }
  }
}


// ── API call ──────────────────────────────────────────────────────────────────
async function postStep(payload: object): Promise<StepResponse> {
  var api = postStepAPIURL;
  const res = await postStepAPI(api, JSON.stringify(payload))
    .catch((error) => {
      throw new Error(`HTTP ${error}`);
    });

  // const data = await res.json();
  // if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res as Promise<StepResponse>;
}

// ── Progress bar update ───────────────────────────────────────────────────────
function updateProgressBar(
  blockId: string,
  total: number,
  nextIndex: number,
  percent: number
): void {
  const block = getBlockEl(blockId);
  if (!block) return;

  const label = block.querySelector<HTMLElement>(".saig-o-quiz__progress-step-label");
  const pct = block.querySelector<HTMLElement>(".saig-o-quiz__progress-percent");
  const fill = block.querySelector<HTMLElement>(".saig-o-quiz__progress-fill");

  if (label) label.textContent = `Question ${nextIndex + 1} of ${total}`;
  if (pct) pct.textContent = `${percent}%`;
  if (fill) fill.style.width = `${percent}%`;
}

// ── Fade & swap the question panel HTML ───────────────────────────────────────
async function swapPanel(blockId: string, html: string): Promise<void> {
  const panel = document.querySelector<HTMLElement>(".saig-o-quiz__Quesions-panel");
  if (!panel) return;

  panel.classList.add("msq-fade");
  await delay(250);
  panel.innerHTML = html;
  panel.classList.remove("msq-fade");
}

// ── Render the inline answer summary ─────────────────────────────────────────
function renderSummary(
  blockId: string
): void {


  const block = getBlockEl(blockId);
  if (!block) return;

  const preogressdiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__progress");
  preogressdiv && (preogressdiv.style.display = "none");

  const titleDiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__titlecontent");
  titleDiv && (titleDiv.style.display = "none");

  const ansText = block?.querySelector<HTMLDivElement>(".saig-o-quiz__recommendedcontent");
  ansText && (ansText.style.display = "block");

  block.querySelector<HTMLButtonElement>(".saig-o-quiz__btn-retake")
    ?.addEventListener("click", () => onRestart(blockId));




}

// ── Restart handler ───────────────────────────────────────────────────────────
async function onRestart(blockId: string): Promise<void> {
  const state = blockStates.get(blockId);
  if (!state) return;

  const payload = {
    BlockContentLink: blockId,
    CurrentIndex: 0,
    Direction: "retake",
    IsContactForm: false,
    MultiAnswers: {},
  };


  const block = getBlockEl(blockId);
  if (!block) return;
  const preogressdiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__progress");
  preogressdiv && (preogressdiv.style.display = "block");

  const titleDiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__titlecontent");
  titleDiv && (titleDiv.style.display = "flex");

  const ansDiv = block?.querySelector<HTMLDivElement>(".saig-o-quiz__recommendedcontent");
  ansDiv && (ansDiv.style.display = "none");

  const data = await postStep(payload);
  updateProgressBar(blockId, state.total, data.nextIndex, data.progressPercent);
  await swapPanel(blockId, data.html);
  state.current = data.nextIndex;
  state.multiAnswers = {};
  bindPanelEvents(blockId);
}

// ── Disable / enable the Next button ─────────────────────────────────────────
function setNextButtonState(blockId: string, disabled: boolean): void {
  const panel = getPanelEl(blockId);
  const btn = panel?.querySelector<HTMLButtonElement>(".saig-o-quiz__nav-btn-next");
  if (btn) btn.disabled = disabled;
}

// ── DOM helpers ───────────────────────────────────────────────────────────────
function getBlockEl(blockId: string): HTMLElement | null {
  return document.getElementById(`msq-${blockId}`);
}

function getPanelEl(blockId: string): HTMLElement | null {
  return document.getElementById(`msq-panel-${blockId}`);
}


// ── Utility ───────────────────────────────────────────────────────────────────
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Auto-init on DOMContentLoaded ────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll<HTMLElement>(".saig-o-quiz").forEach((el) => {
    const blockId = el.dataset.blockId ?? "";
    const total = parseInt(el.dataset.total ?? "0", 10);
    postStepAPIURL = el.dataset.postStepApi ?? "";
    postContactAPIURL = el.dataset.postContactApi ?? "";
    if (blockId && total > 0) initQuiz(blockId, total);
  });
});